import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../AuthContext';
import { authedFetch } from '../../utils/authedFetch';
import { supabase } from '../../supabaseClient';
import {
  TextField,
  Button,
  Typography,
  Box,
  Paper,
  Stack,
  CircularProgress,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import EditIcon from '@mui/icons-material/Edit';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import PersonIcon from '@mui/icons-material/Person';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import FeedbackButton from '../../utils/feedbackButton';

const ACCENT = 'rgb(219, 234, 254)';
const ACCENT_BORDER = '#60A5FA';

export default function FacilitatorDash() {
  const { user, session, authLoading } = useAuth();
  const navigate = useNavigate();
  const API_BASE = process.env.REACT_APP_API_URL;

  const [patients, setPatients] = useState([]);
  const [patientsLoading, setPatientsLoading] = useState(false);
  const [patientsErr, setPatientsErr] = useState(null);

  const [scenarios, setScenarios] = useState([]);
  const [selectedScenario, setSelectedScenario] = useState(null);
  const [editScenario, setEditScenario] = useState(null);
  const [editPatient, setEditPatient] = useState(null);

  const [loadingScenarios, setLoadingScenarios] = useState(false);
  const [scenarioErr, setScenarioErr] = useState(null);

  const MAX_IMAGE_UPLOADS = 5;
  const [selectedImages, setSelectedImages] = useState([]);
  const [imageUploadLoading, setImageUploadLoading] = useState(false);
  const [imageUploadError, setImageUploadError] = useState('');
  const [imageUploadSuccess, setImageUploadSuccess] = useState('');
  const [pendingImages, setPendingImages] = useState([]);
  const [reviewImagesOpen, setReviewImagesOpen] = useState(false);

  const canStartLive = !!selectedScenario;

  useEffect(() => {
    if (authLoading) return;
    if (!session || !user) {
      navigate('/login', { replace: true });
    }
  }, [authLoading, session, user, navigate]);

  useEffect(() => {
    if (authLoading || !session || !user) return;

    const loadPatients = async () => {
      setPatientsLoading(true);
      setPatientsErr(null);

      try {
        const res = await authedFetch({
          accessToken: session.access_token,
          apiBase: API_BASE,
          path: '/api/getpatients',
          navigate,
        });

        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const data = await res.json();
        setPatients(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Failed to retrieve patients:', err);
        setPatientsErr('Could not load patients');
        setPatients([]);
      } finally {
        setPatientsLoading(false);
      }
    };

    const loadScenarios = async () => {
      setLoadingScenarios(true);
      setScenarioErr(null);

      try {
        const res = await authedFetch({
          accessToken: session.access_token,
          apiBase: API_BASE,
          path: '/api/getscenarios',
          navigate,
        });

        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const data = await res.json();
        setScenarios(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Failed to retrieve scenarios:', err);
        setScenarioErr('Could not load scenarios');
        setScenarios([]);
      } finally {
        setLoadingScenarios(false);
      }
    };

    loadPatients();
    loadScenarios();
  }, [authLoading, session, user, API_BASE, navigate]);

  const handleStartLive = () => {
    if (!selectedScenario) return;

    navigate('/app/live', {
      state: {
        scenarioId: selectedScenario.id,
      },
    });
  };

  const handleCreateScenario = () => {
    navigate('/app/create/scenario', {
      state: {
        flow: 'scenario-create',
        patientId: null,
      },
    });
  };

  const handleEditScenario = async () => {
    if (!editScenario) return;

    try {
      const query = new URLSearchParams({ scenarioid: editScenario.id }).toString();

      const res = await authedFetch({
        accessToken: session?.access_token,
        apiBase: API_BASE,
        path: `/api/patientfromscenario?${query}`,
        navigate,
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const patient = await res.json();

      navigate('/app/create/scenario', {
        state: {
          flow: 'scenario-edit',
          scenarioId: editScenario.id,
          patientId: patient?.id ?? null,
        },
      });
    } catch (err) {
      console.error('Failed to get patient for scenario:', err);
    }
  };

  const handleCreatePatient = () => {
    navigate('/app/create/epr', {
      state: {
        flow: 'patient-create',
        patientId: null,
      },
    });
  };

  const handleEditPatient = () => {
    if (!editPatient) return;

    navigate('/app/create/epr', {
      state: {
        flow: 'patient-edit',
        patientId: editPatient.id,
        patientName: editPatient.name,
      },
    });
  };

  // Checks image uploads
  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files || []);

    setImageUploadError('');
    setImageUploadSuccess('');

    if (files.length > MAX_IMAGE_UPLOADS) {
      setSelectedImages([]);
      setImageUploadError(`You can upload a maximum of ${MAX_IMAGE_UPLOADS} images at once.`);
      e.target.value = '';
      return;
    }

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    const maxSizeBytes = 5 * 1024 * 1024;

    const invalidFile = files.find((file) => !allowedTypes.includes(file.type));
    if (invalidFile) {
      setSelectedImages([]);
      setImageUploadError(`${invalidFile.name} is not a supported image type.`);
      e.target.value = '';
      return;
    }

    const oversizedFile = files.find((file) => file.size > maxSizeBytes);
    if (oversizedFile) {
      setSelectedImages([]);
      setImageUploadError(`${oversizedFile.name} is larger than 5MB.`);
      e.target.value = '';
      return;
    }

    setSelectedImages(files);
    e.target.value = '';
  };

  // Uploads images to backend before user inputs title/modality/etc.
  const handleUploadImages = async () => {
    if (selectedImages.length === 0) return;

    setImageUploadLoading(true);
    setImageUploadError('');
    setImageUploadSuccess('');

    try {
      const uploaded = [];

      for (const file of selectedImages) {
        const createRes = await authedFetch({
          accessToken: session.access_token,
          apiBase: API_BASE,
          path: '/api/images/create-upload-url',
          navigate,
          options: {
            method: 'POST',
            body: JSON.stringify({
              fileName: file.name,
              contentType: file.type,
              title: file.name,
            }),
          },
        });

        const uploadInfo = await createRes.json();

        if (!createRes.ok) {
          throw new Error(uploadInfo.error || `Could not prepare upload for ${file.name}.`);
        }

        const { error: uploadError } = await supabase.storage
          .from(uploadInfo.bucket)
          .uploadToSignedUrl(uploadInfo.path, uploadInfo.token, file, {
            contentType: file.type,
          });

        if (uploadError) {
          throw new Error(uploadError.message || `Upload failed for ${file.name}.`);
        }

        uploaded.push({
          localId: `${file.name}-${file.size}-${Date.now()}`,
          fileName: file.name,
          previewUrl: URL.createObjectURL(file),
          bucket: uploadInfo.bucket,
          path: uploadInfo.path,
          title: file.name,
          modality: '',
          image_type: '',
          addReport: false,
          report: '',
        });
      }

      setPendingImages(uploaded);
      setSelectedImages([]);
      setReviewImagesOpen(true);
    } catch (err) {
      console.error('Failed to upload images:', err);
      setImageUploadError(err.message || 'Could not upload images.');
    } finally {
      setImageUploadLoading(false);
    }
  };

  // Image upload helpers
  const updatePendingImage = (localId, updates) => {
    setPendingImages((prev) =>
      prev.map((img) => (img.localId === localId ? { ...img, ...updates } : img))
    );
  };

  const handleSaveReviewedImages = async () => {
    const missingRequired = pendingImages.find(
      (img) => !img.title.trim() || !img.modality.trim()
    );

    if (missingRequired) {
      setImageUploadError('Each uploaded image needs a title and modality.');
      return;
    }

    setImageUploadLoading(true);
    setImageUploadError('');
    setImageUploadSuccess('');

    try {
      const res = await authedFetch({
        accessToken: session.access_token,
        apiBase: API_BASE,
        path: '/api/images/confirm-upload',
        navigate,
        options: {
          method: 'POST',
          body: JSON.stringify({
            images: pendingImages.map((img) => ({
              bucket: img.bucket,
              path: img.path,
              title: img.title.trim(),
              modality: img.modality,
              image_type: img.image_type?.trim() || null,
              report: img.addReport ? img.report?.trim() || null : null,
            })),
          }),
        },
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Could not save image details.');
      }

      pendingImages.forEach((img) => URL.revokeObjectURL(img.previewUrl));

      setPendingImages([]);
      setReviewImagesOpen(false);
      setImageUploadSuccess('Images uploaded and saved successfully.');
    } catch (err) {
      console.error('Failed to save reviewed images:', err);
      setImageUploadError(err.message || 'Could not save image details.');
    } finally {
      setImageUploadLoading(false);
    }
  };

  const handleCancelReviewedImages = () => {
    pendingImages.forEach((img) => URL.revokeObjectURL(img.previewUrl));
    setPendingImages([]);
    setReviewImagesOpen(false);
  };

  if (authLoading) {
    return (
      <Box sx={{ height: '100%', display: 'grid', placeItems: 'center' }}>
        <Stack spacing={2} alignItems="center">
          <CircularProgress />
          <Typography>Loading dashboard...</Typography>
        </Stack>
      </Box>
    );
  }

  if (!session || !user) return null;

  return (
    <Box
      sx={{
        height: '100%',
        width: '100%',
        bgcolor: '#F8FAFF',
        p: 2,
        overflowY: 'auto',
        overflowX: 'hidden',
        paddingTop: '25px',
      }}
    >
      <Stack spacing={2} sx={{ width: '95%', mx: 'auto', alignItems: 'center' }}>
        <Box sx={{ width: '100%', maxWidth: '1100px' }}>
          <SectionCard
            title="Start simulation"
            subtitle="Launch a prebuilt scenario and connect to the participant screen."
          >
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ pt: 1.5, px: 2 }}
            >
              Select a scenario to begin. A 5-digit code will be provided on the next screen.
            </Typography>

            <Box sx={{ p: 2 }}>
              <Stack
                direction={{ xs: 'column', lg: 'row' }}
                spacing={2}
                alignItems={{ xs: 'stretch', lg: 'center' }}
              >
                <Box sx={{ flex: 1 }}>
                  <Autocomplete
                    options={scenarios}
                    value={selectedScenario}
                    onChange={(_, val) => setSelectedScenario(val)}
                    getOptionLabel={(opt) => opt?.title ?? ''}
                    isOptionEqualToValue={(o, v) => o.id === v.id}
                    loading={loadingScenarios}
                    noOptionsText={
                      loadingScenarios ? 'Loading...' : scenarioErr || 'No scenarios'
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select scenario"
                        placeholder="Search scenarios..."
                        error={!!scenarioErr}
                      />
                    )}
                  />
                </Box>

                <Button
                  variant="contained"
                  startIcon={<PlayArrowIcon />}
                  disabled={!canStartLive}
                  onClick={handleStartLive}
                  sx={{
                    textTransform: 'none',
                    minWidth: 170,
                    height: 56,
                  }}
                >
                  Start scenario
                </Button>
              </Stack>

              {scenarioErr && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {scenarioErr}
                </Alert>
              )}
            </Box>
          </SectionCard>
        </Box>

        <Box sx={{ width: '100%', maxWidth: '1100px' }}>
          <SectionCard
            title="Manage content"
            subtitle="Create, edit, and upload simulation content."
          >
            <Box
              sx={{
                p: 3,
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                gap: 3,
              }}
            >
              <ContentPanel
                icon={<NoteAddIcon sx={{ color: 'text.secondary' }} />}
                title="Scenarios"
              >
                <Typography sx={{ fontWeight: 600, mb: 1 }}>
                  Edit existing scenario
                </Typography>

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr auto',
                    gap: 1,
                    alignItems: 'center',
                  }}
                >
                  <Autocomplete
                    options={scenarios}
                    value={editScenario}
                    onChange={(_, val) => setEditScenario(val)}
                    getOptionLabel={(opt) => opt?.title ?? ''}
                    isOptionEqualToValue={(o, v) => o.id === v.id}
                    loading={loadingScenarios}
                    noOptionsText={
                      loadingScenarios ? 'Loading...' : scenarioErr || 'No scenarios'
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        label="Select scenario"
                        placeholder="Search scenarios..."
                        error={!!scenarioErr}
                      />
                    )}
                  />

                  <Button
                    variant="outlined"
                    startIcon={<EditIcon />}
                    onClick={handleEditScenario}
                    disabled={!editScenario}
                    sx={{ textTransform: 'none', minWidth: 100, height: 40 }}
                  >
                    Edit
                  </Button>
                </Box>

                <DividerLine />

                <Typography sx={{ fontWeight: 600, mb: 1 }}>
                  Create new scenario
                </Typography>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Start building a new scenario. You can attach a patient later in the flow.
                </Typography>

                <Button
                  variant="contained"
                  color="success"
                  startIcon={<PlaylistAddIcon />}
                  onClick={handleCreateScenario}
                  sx={{ textTransform: 'none' }}
                >
                  Create scenario
                </Button>
              </ContentPanel>

              <ContentPanel
                icon={<PersonIcon sx={{ color: 'text.secondary' }} />}
                title="Patients"
              >
                <Typography sx={{ fontWeight: 600, mb: 1 }}>
                  Edit existing patient
                </Typography>

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr auto',
                    gap: 1,
                    alignItems: 'center',
                  }}
                >
                  <Autocomplete
                    options={patients}
                    value={editPatient}
                    onChange={(_, val) => setEditPatient(val)}
                    getOptionLabel={(opt) => opt?.name ?? ''}
                    isOptionEqualToValue={(o, v) => o.id === v.id}
                    loading={patientsLoading}
                    noOptionsText={
                      patientsLoading ? 'Loading...' : patientsErr || 'No patients'
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        label="Select patient"
                        placeholder="Search patients..."
                        error={!!patientsErr}
                      />
                    )}
                  />

                  <Button
                    variant="outlined"
                    startIcon={<EditIcon />}
                    onClick={handleEditPatient}
                    disabled={!editPatient}
                    sx={{ textTransform: 'none', minWidth: 100, height: 40 }}
                  >
                    Edit
                  </Button>
                </Box>

                <DividerLine />

                <Typography sx={{ fontWeight: 600, mb: 1 }}>
                  Create new patient
                </Typography>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Create a new patient record and populate the EPR data.
                </Typography>

                <Button
                  variant="contained"
                  color="success"
                  startIcon={<PersonAddAltIcon />}
                  onClick={handleCreatePatient}
                  sx={{ textTransform: 'none' }}
                >
                  Create patient
                </Button>
              </ContentPanel>

              <Box sx={{ gridColumn: { xs: '1', md: '1 / -1' } }}>
                <ContentPanel
                  icon={<ImageOutlinedIcon sx={{ color: 'text.secondary' }} />}
                  title="Images"
                >
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', md: '1.2fr 1fr' },
                      gap: 2,
                      alignItems: 'stretch',
                    }}
                  >
                    <Box
                      sx={{
                        border: '1px dashed #93C5FD',
                        borderRadius: 3,
                        bgcolor: 'white',
                        p: 3,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        textAlign: 'center',
                        minHeight: 180,
                      }}
                    >
                      <CloudUploadIcon sx={{ fontSize: 42, color: ACCENT_BORDER, mb: 1 }} />

                      <Typography sx={{ fontWeight: 800, mb: 0.5 }}>
                        Upload simulation images
                      </Typography>

                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ maxWidth: 420, mb: 2 }}
                      >
                        Add radiology, ECGs, clinical images, or other media for use in scenarios. (maximum of 5 uploads at a time)
                      </Typography>

                      <Button
                        component="label"
                        variant="outlined"
                        startIcon={<CloudUploadIcon />}
                        sx={{ textTransform: 'none' }}
                      >
                        Choose images
                        <input
                          hidden
                          type="file"
                          accept="image/png,image/jpeg,image/webp"
                          multiple
                          onChange={handleImageSelect}
                        />
                      </Button>
                    </Box>

                    <Box
                      sx={{
                        display:'flex',
                        flexDirection:'column',
                        alignItems:'center',
                        justifyContent:'center',
                        border: `1px solid ${ACCENT_BORDER}`,
                        borderRadius: 3,
                        bgcolor: '#F8FAFF',
                        p: 2,
                      }}
                    >
                      <Typography sx={{ fontWeight: 700, mb: 1 }}>
                        Selected files
                      </Typography>

                      {selectedImages.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          No images selected.
                        </Typography>
                      ) : (
                        <Stack spacing={1} sx={{ mb: 2 }}>
                          {selectedImages.map((file) => (
                            <Chip
                              key={`${file.name}-${file.size}`}
                              label={`${file.name} (${Math.round(file.size / 1024)} KB)`}
                              variant="outlined"
                              sx={{
                                justifyContent: 'flex-start',
                                maxWidth: '100%',
                              }}
                            />
                          ))}
                        </Stack>
                      )}

                      {imageUploadError && (
                        <Alert severity="error" sx={{ mb: 1.5 }}>
                          {imageUploadError}
                        </Alert>
                      )}

                      {imageUploadSuccess && (
                        <Alert severity="success" sx={{ mb: 1.5 }}>
                          {imageUploadSuccess}
                        </Alert>
                      )}

                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                        <Button
                          variant="contained"
                          disabled={selectedImages.length === 0 || imageUploadLoading}
                          onClick={handleUploadImages}
                          startIcon={
                            imageUploadLoading ? (
                              <CircularProgress size={16} color="inherit" />
                            ) : (
                              <CloudUploadIcon />
                            )
                          }
                          sx={{ textTransform: 'none' }}
                        >
                          {imageUploadLoading ? 'Uploading...' : 'Upload images'}
                        </Button>

                        <Button
                          variant="outlined"
                          disabled={selectedImages.length === 0 || imageUploadLoading}
                          onClick={() => {
                            setSelectedImages([]);
                            setImageUploadError('');
                            setImageUploadSuccess('');
                          }}
                          sx={{ textTransform: 'none' }}
                        >
                          Clear
                        </Button>
                      </Stack>
                    </Box>
                  </Box>
                </ContentPanel>
              </Box>
            </Box>
          </SectionCard>
        </Box>
      </Stack>

      <Box
        sx={{
          position: 'fixed',
          bottom: 20,
          left: 20,
          zIndex: 2000,
        }}
      >
        <FeedbackButton page="Facilitator dash" />
      </Box>


      <Dialog
        open={reviewImagesOpen}
        onClose={imageUploadLoading ? undefined : handleCancelReviewedImages}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 800 }}>
          Review uploaded images
        </DialogTitle>

        <DialogContent dividers>
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Check each image and add the details needed before it appears in your image library.
            </Typography>

            {imageUploadError && (
              <Alert severity="error">
                {imageUploadError}
              </Alert>
            )}

            {pendingImages.map((img, index) => (
              <Paper
                key={img.localId}
                elevation={0}
                sx={{
                  border: '1px solid #d1d5db',
                  borderRadius: 2,
                  p: 2,
                  bgcolor: '#F8FAFF',
                }}
              >
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={2}
                  alignItems="flex-start"
                >
                  <Box
                    sx={{
                      width: { xs: '100%', md: 180 },
                      flexShrink: 0,
                    }}
                  >
                    <Box
                      component="img"
                      src={img.previewUrl}
                      alt={img.title}
                      sx={{
                        width: '100%',
                        height: 140,
                        objectFit: 'contain',
                        bgcolor: 'white',
                        border: '1px solid #e5e7eb',
                        borderRadius: 2,
                      }}
                    />

                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        display: 'block',
                        mt: 0.75,
                        wordBreak: 'break-word',
                      }}
                    >
                      Image {index + 1}: {img.fileName}
                    </Typography>
                  </Box>

                  <Stack spacing={1.5} sx={{ flex: 1, width: '100%' }}>
                    <TextField
                      label="Scan title"
                      value={img.title}
                      required
                      fullWidth
                      onChange={(e) =>
                        updatePendingImage(img.localId, { title: e.target.value })
                      }
                    />

                    <Autocomplete
                      options={['xray', 'uss', 'mri', 'ct', 'ecg', 'Other']}
                      value={img.modality || null}
                      onChange={(_, value) =>
                        updatePendingImage(img.localId, { modality: value || '' })
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Modality"
                          required
                        />
                      )}
                    />

                    <TextField
                      label="Image type"
                      placeholder="e.g. Chest X-ray, 12-lead ECG, CT head"
                      value={img.image_type}
                      fullWidth
                      onChange={(e) =>
                        updatePendingImage(img.localId, { image_type: e.target.value })
                      }
                    />

                    <FormControlLabel
                      control={
                        <Switch
                          checked={img.addReport}
                          onChange={(e) =>
                            updatePendingImage(img.localId, {
                              addReport: e.target.checked,
                              report: e.target.checked ? img.report : '',
                            })
                          }
                        />
                      }
                      label="Add report"
                    />

                    {img.addReport && (
                      <TextField
                        label="Report"
                        placeholder="Enter report text..."
                        value={img.report}
                        multiline
                        minRows={3}
                        fullWidth
                        onChange={(e) =>
                          updatePendingImage(img.localId, { report: e.target.value })
                        }
                      />
                    )}
                  </Stack>
                </Stack>
              </Paper>
            ))}
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={handleCancelReviewedImages}
            disabled={imageUploadLoading}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={handleSaveReviewedImages}
            disabled={imageUploadLoading}
            startIcon={
              imageUploadLoading ? (
                <CircularProgress size={16} color="inherit" />
              ) : null
            }
            sx={{ textTransform: 'none' }}
          >
            {imageUploadLoading ? 'Saving...' : 'Save images'}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}

function SectionCard({ title, subtitle, children }) {
  return (
    <Paper
      elevation={0}
      sx={{
        border: `1px solid ${ACCENT_BORDER}`,
        borderRadius: 3,
        bgcolor: 'white',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.25,
          bgcolor: ACCENT,
          borderBottom: `1px solid ${ACCENT_BORDER}`,
          display: 'flex',
          alignItems: { xs: 'flex-start', md: 'baseline' },
          justifyContent: 'space-between',
          gap: 2,
          flexDirection: { xs: 'column', md: 'row' },
        }}
      >
        <Typography sx={{ fontWeight: 700 }}>{title}</Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </Box>
      {children}
    </Paper>
  );
}

function ContentPanel({ icon, title, children }) {
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        {icon}
        <Typography sx={{ fontWeight: 700, fontSize: '1.05rem' }}>
          {title}
        </Typography>
      </Box>

      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${ACCENT_BORDER}`,
          borderRadius: 2,
          p: 2,
          bgcolor: '#F8FAFF',
        }}
      >
        {children}
      </Paper>
    </Box>
  );
}

function DividerLine() {
  return <Box sx={{ my: 2, borderTop: '1px solid #E5E7EB' }} />;
}